import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core"
import SaveIcon from "@material-ui/icons/Save"
import React, { useEffect, useState } from "react"
import {
    alternarConexao,
    conectarIntegrador,
    Conexoes,
    ConexaoPdv,
    gerarMerchantIdLogistica,
    Logistica,
    obterConexoes,
    obterLogistica,
    regerarSecret,
    salvarLogistica,
    salvarObservacaoGeralNosItens,
} from "../../../services/openDelivery"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"
import { useSnackbar } from "../../../utils/notificacoes"

const logisticaVazia = {
    integradorId: 0,
    baseUrl: "",
    clientId: "",
    clientSecret: "",
    merchantId: "",
    automatico: true,
}

/**
 * Conexão da loja com os parceiros Open Delivery.
 *
 * São dois blocos com direções opostas:
 *
 * - PDV: nós geramos as credenciais e o lojista as informa no sistema dele. A loja pode manter
 *   vários PDVs conectados ao mesmo tempo.
 * - Logística: a praça de entrega fornece as credenciais e nós as guardamos para chamar a API
 *   dela. No máximo uma por loja — duas chamariam dois entregadores para o mesmo pedido.
 */
export function IntegracaoOpenDeliveryForm() {
    const classes = useIntegracaoStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [conexoes, setConexoes] = useState<Conexoes | null>(null)
    const [logistica, setLogistica] = useState<Logistica | null>(null)
    const [formLogistica, setFormLogistica] = useState(logisticaVazia)
    const [salvando, setSalvando] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info" | "warning") =>
        enqueueSnackbar(msg, { variant: tipo })

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const [resConexoes, resLogistica] = await Promise.all([
                obterConexoes(autorizacao),
                obterLogistica(autorizacao),
            ])

            if ("retornoErro" in resConexoes)
                alertStart(resConexoes.mensagem || "Erro ao carregar conexões", "error")
            else setConexoes(resConexoes)

            if ("retornoErro" in resLogistica) {
                alertStart(resLogistica.mensagem || "Erro ao carregar logística", "error")
            } else {
                setLogistica(resLogistica)
                const atual = resLogistica.credencial
                setFormLogistica(
                    atual
                        ? {
                              integradorId: atual.integradorId,
                              baseUrl: atual.baseUrl ?? "",
                              clientId: atual.clientId ?? "",
                              clientSecret: atual.clientSecret ?? "",
                              merchantId: atual.merchantId ?? "",
                              automatico: atual.automatico,
                          }
                        : {
                              ...logisticaVazia,
                              integradorId: resLogistica.disponiveis?.[0]?.id ?? 0,
                          }
                )
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const executar = async (
        acao: () => Promise<Conexoes | { retornoErro: number; mensagem: string }>,
        mensagemSucesso: string
    ) => {
        setLoading(true)
        try {
            const res = await acao()
            if ("retornoErro" in res) alertStart(res.mensagem || "Erro na operação", "error")
            else {
                setConexoes(res)
                alertStart(mensagemSucesso, "success")
            }
        } finally {
            setLoading(false)
        }
    }

    const handleConectar = (integradorId: number) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        executar(
            () => conectarIntegrador(autorizacao, integradorId),
            "Credenciais geradas. Informe estes dados no sistema do parceiro."
        )
    }

    const handleRegerarSecret = (credencialId: number, nome: string) => {
        if (
            !window.confirm(
                `Gerar um novo Client Secret invalida o atual. "${nome}" vai parar de se conectar ` +
                    "até ser reconfigurado. Os demais parceiros não são afetados. Continuar?"
            )
        ) {
            return
        }
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        executar(
            () => regerarSecret(autorizacao, credencialId),
            "Novo Client Secret gerado. Atualize o cadastro no parceiro."
        )
    }

    const handleCopiarDados = (credencial: ConexaoPdv) => {
        const texto = [
            `Estabelecimento: ${conexoes?.estabelecimentoNome ?? ""}`,
            `URL base: ${conexoes?.urlBase ?? ""}`,
            `App id: ${conexoes?.appId ?? ""}`,
            "",
            `Cliente ID: ${credencial.clientId}`,
            `Cliente Secret: ${credencial.clientSecret}`,
            `Merchant ID: ${credencial.merchantId}`,
        ].join("\n")
        navigator.clipboard.writeText(texto).then(
            () => alertStart("Dados da integração copiados!", "success"),
            () => alertStart("Não foi possível copiar.", "error")
        )
    }

    const handleAlternarConexao = (credencialId: number) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        executar(() => alternarConexao(autorizacao, credencialId), "Conexão atualizada.")
    }

    const handleObservacaoGeralNosItens = (ativo: boolean) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        executar(
            () => salvarObservacaoGeralNosItens(autorizacao, ativo),
            "Configuração de impressão salva."
        )
    }

    const handleGerarMerchantId = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        const res = await gerarMerchantIdLogistica(autorizacao)
        if ("retornoErro" in res) alertStart(res.mensagem || "Erro ao gerar", "error")
        else setFormLogistica(prev => ({ ...prev, merchantId: res.merchantId }))
    }

    const handleCopiarMerchantId = () => {
        navigator.clipboard.writeText(formLogistica.merchantId).then(
            () => alertStart("Merchant Id copiado.", "success"),
            () => alertStart("Não foi possível copiar.", "error")
        )
    }

    const handleSalvarLogistica = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        if (!formLogistica.baseUrl.trim() || !formLogistica.clientId.trim()) {
            alertStart("Informe ao menos a URL base e o Client ID do parceiro.", "error")
            return
        }
        setSalvando(true)
        try {
            const res = await salvarLogistica(autorizacao, formLogistica)
            if ("retornoErro" in res) alertStart(res.mensagem || "Erro ao salvar", "error")
            else {
                setLogistica(res)
                alertStart("Logística configurada.", "success")
            }
        } finally {
            setSalvando(false)
        }
    }

    const campoSomenteLeitura = (label: string, valor: string) => (
        <TextField
            label={label}
            variant="outlined"
            size="small"
            fullWidth
            value={valor ?? ""}
            InputProps={{ readOnly: true }}
            onFocus={e => e.target.select()}
        />
    )

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Integração Open Delivery
                </Typography>

                <Typography variant="body2" color="textSecondary" paragraph>
                    O Open Delivery é o padrão aberto de integração entre cardápios digitais e
                    sistemas de PDV. Conecte um parceiro abaixo e informe os quatro dados gerados na
                    tela de conexão do sistema dele. Você pode manter mais de um parceiro conectado
                    ao mesmo tempo — cada um recebe os mesmos pedidos, de forma independente.
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Dados da sua loja
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                {campoSomenteLeitura(
                                    "Nome do Estabelecimento",
                                    conexoes?.estabelecimentoNome ?? ""
                                )}
                            </Grid>
                            {conexoes?.estabelecimentoCnpj && (
                                <Grid item xs={12} sm={6}>
                                    {campoSomenteLeitura("CNPJ", conexoes.estabelecimentoCnpj)}
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                {campoSomenteLeitura(
                                    "URL base do serviço de pedidos",
                                    conexoes?.urlBase ?? ""
                                )}
                                <Typography variant="caption" color="textSecondary">
                                    A mesma para todos os parceiros.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {campoSomenteLeitura("App ID", conexoes?.appId ?? "")}
                                <Typography variant="caption" color="textSecondary">
                                    Identificador da Cliente Fiel como Ordering Application no Open
                                    Delivery, o mesmo para todas as lojas e parceiros — não é
                                    específico desta loja.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {conexoes?.credenciais?.map(credencial => (
                    <Paper className={classes.configCard} key={credencial.id}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                {credencial.integradorNome}
                            </Typography>
                            {!credencial.ativo && (
                                <Box ml={2}>
                                    <Chip size="small" label="desativado" color="default" />
                                </Box>
                            )}
                        </Box>
                        <Box className={classes.cardContent}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    {campoSomenteLeitura("Client ID", credencial.clientId)}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {campoSomenteLeitura("Client Secret", credencial.clientSecret)}
                                </Grid>
                                <Grid item xs={12}>
                                    {campoSomenteLeitura("Merchant Id", credencial.merchantId)}
                                </Grid>
                            </Grid>

                            <Box display="flex" justifyContent="flex-end" mt={2} gridGap={8}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleCopiarDados(credencial)}
                                >
                                    Copiar dados da Integração
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleAlternarConexao(credencial.id)}
                                >
                                    {credencial.ativo ? "Desativar" : "Reativar"}
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                        handleRegerarSecret(
                                            credencial.id,
                                            credencial.integradorNome
                                        )
                                    }
                                >
                                    Gerar novo Client Secret
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                ))}

                {conexoes && conexoes.credenciais?.length > 0 && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Impressão no PDV
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!conexoes.observacaoGeralNosItens}
                                        onChange={e =>
                                            handleObservacaoGeralNosItens(e.target.checked)
                                        }
                                        color="primary"
                                    />
                                }
                                label="Imprimir a observação geral do pedido junto de cada item"
                            />
                            <Typography variant="caption" color="textSecondary" component="p">
                                Marque se o seu PDV mostra a observação feita no fim do pedido só no
                                cupom do pedido e ela não sai na comanda da cozinha. A observação é
                                repetida em todos os itens. Vale para todos os parceiros conectados.
                            </Typography>
                        </Box>
                    </Paper>
                )}

                {conexoes && conexoes.disponiveis?.length > 0 && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Conectar um novo parceiro
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            {conexoes.disponiveis.map(integrador => (
                                <Box key={integrador.id} mb={1}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => handleConectar(integrador.id)}
                                    >
                                        Conectar {integrador.nome}
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}

                {conexoes &&
                    conexoes.credenciais?.length === 0 &&
                    conexoes.disponiveis?.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                            Nenhum parceiro Open Delivery homologado no momento. Fale com o suporte
                            para solicitar a homologação do seu PDV.
                        </Typography>
                    )}

                {logistica && logistica.disponiveis?.length > 0 && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Praça de entrega (logística)
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                Aqui a direção é oposta à de cima: <strong>nós</strong> chamamos o
                                entregador na API do parceiro, usando as credenciais que{" "}
                                <strong>ele</strong> fornece. Por isso os campos são digitados — só
                                o Merchant Id é gerado por nós. Se a sua loja já usa Uber Direct ou outra entrega, não
                                configure aqui — os dois caminhos acionariam entregadores em
                                paralelo.
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="URL base da API do parceiro"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={formLogistica.baseUrl}
                                        onChange={e =>
                                            setFormLogistica(prev => ({
                                                ...prev,
                                                baseUrl: e.target.value,
                                            }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Client ID fornecido pelo parceiro"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={formLogistica.clientId}
                                        onChange={e =>
                                            setFormLogistica(prev => ({
                                                ...prev,
                                                clientId: e.target.value,
                                            }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Client Secret fornecido pelo parceiro"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={formLogistica.clientSecret}
                                        onChange={e =>
                                            setFormLogistica(prev => ({
                                                ...prev,
                                                clientSecret: e.target.value,
                                            }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Merchant Id da loja no parceiro"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={formLogistica.merchantId}
                                        onChange={e =>
                                            setFormLogistica(prev => ({
                                                ...prev,
                                                merchantId: e.target.value,
                                            }))
                                        }
                                        helperText="Gerado por nós: informe este mesmo valor no cadastro do parceiro."
                                    />
                                    <Box display="flex" mt={1} style={{ gap: 8 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleGerarMerchantId}
                                        >
                                            Gerar
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={!formLogistica.merchantId}
                                            onClick={handleCopiarMerchantId}
                                        >
                                            Copiar
                                        </Button>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formLogistica.automatico}
                                                onChange={e =>
                                                    setFormLogistica(prev => ({
                                                        ...prev,
                                                        automatico: e.target.checked,
                                                    }))
                                                }
                                                color="primary"
                                            />
                                        }
                                        label="Chamar o entregador quando o pedido ficar pronto"
                                    />
                                </Grid>
                            </Grid>

                            <Box display="flex" justifyContent="flex-end" mt={2}>
                                <Button
                                    className={classes.saveButton}
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSalvarLogistica}
                                    disabled={salvando}
                                >
                                    {salvando ? "Salvando..." : "Salvar"}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    )
}
